import classNames from "classnames";
import JsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Papa from "papaparse";
import { useEffect, useRef, useState } from "react";
import { Column, Row, useExpanded, useGlobalFilter, usePagination, useRowSelect, useSortBy, useTable } from "react-table";
import { useExportData } from "react-table-plugins";
import { utils, writeFile } from "xlsx";
import { IndeterminateCheckbox } from "./Checkbox/IndeterminateCheckbox";
import { IndeterminateBodyCheckbox } from "./Checkbox/IndeterminateBodyCheckbox";
import { Pagination } from "./Pagination";
import { Toolbar } from "./Toolbar";
import { Button, Col, Modal, Row as BSRow, Table } from "react-bootstrap";
import { TimedMessage } from "../TimedMessage";
import Spinner from "../Spinner";

export type CellFormatter<T extends Object = {}> = { row: Row<T>; };

type TableProps<TableValues> = {
    // Table atributtes
    data?: any;
    table?: any;
    errors?: string;
    loading?: boolean;
    tableClass?: string
    className?: string;
    theadClass?: string;
    pageSize?: number;
    isSortable?: boolean;
    stateReference?: any;
    maxHeight?: number;
    pagination?: boolean;
    autoHeight?: boolean;
    isExpandable?: boolean;
    columns: ReadonlyArray<Column>;
    updateTableRows?: (state: any) => void;
    // Search Props 
    isSearchable?: boolean;
    searchBoxClass?: string;
    searchPlaceHolder?: string;
    onQuickAdd?: (value: string) => void;
    allowQuickAdd?: boolean;
    onSearchClick?: (keyword: string) => void;
    keywordChange?: (keyword: string) => void;
    // Toolbar Props
    disableExport?: boolean;
    syncOptions?: () => void;
    addNewRow?: () => void;
    customAddButton?: React.ReactNode;
    singleImport?: () => void;
    removeRows?: (deleteCallback: () => void) => void;
    handleDelete?: () => void;
    handleSingleEdit?: () => void;
    handleBulkEdit?: () => void;
    merge?: {
        handleMerge: () => void;
        canMerge: () => boolean;
    }
    importProducts?: () => void;
    reloadTableRows?: () => void;
    importBulkUpdate?: () => void;
    // Select Props
    isSelectable?: boolean;
    selectAllFilter?: boolean;
    selectedRows?: any[];
    deselectedRows?: any[];
    columnCheckSelectAll?: () => void;
    columnCheckDeselectAll?: () => void;
    saveSelectedRows?: (rows: any) => void;
    handleSelectedRows?: (rows: any) => void;
    selectedRowsUpdate?: (rows: any, index: any) => void;
    deselectedRowsUpdate?: (rows: any, index: any) => void;
    // Extra Components Props
    topbarChild?: React.ReactNode;
    filterComponent?: React.ReactNode;
    customSearchButton?: React.ReactNode;
};

export const ProTable = <TableValues extends object = {}>(props: TableProps<TableValues>) => {
    const data = props["data"] || [];
    const table = props["table"] || [];
    const componentRef = useRef(null);
    const loading = props["loading"] || false;
    const className = props["className"] || "";
    const maxHeight = props["maxHeight"] || "";
    const isSortable = props["isSortable"] || false;
    const pagination = props["pagination"] || false;
    const autoHeight = props["autoHeight"] || false;
    const topbarChild = props["topbarChild"] || null;
    const isSelectable = props["isSelectable"] || false;
    const selectedRows = props["selectedRows"] || [];
    const removeRows = props["removeRows"] || null;
    const isSearchable = props["isSearchable"] || false;
    const syncOptions = props["syncOptions"] || false;
    const addNewRow = props["addNewRow"] || false;
    const customAddButton = props["customAddButton"] || null;
    const singleImport = props["singleImport"] || false;
    const isExpandable = props["isExpandable"] || false;
    const selectAllFilter = props["selectAllFilter"] || false;
    const onSearchClick = props["onSearchClick"] || null;
    const disableExport = props["disableExport"] || false;
    const onQuickAdd = props["onQuickAdd"] || null;
    const allowQuickAdd = props["allowQuickAdd"] || null;
    const [showPopup, setShowPopup] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const handleSingleEdit = props["handleSingleEdit"] || false;
    const handleBulkEdit = props["handleBulkEdit"] || false;
    const merge = props["merge"] || false;
    const deselectedRows = props["deselectedRows"] || [];
    const importProducts = props["importProducts"] || false;
    const keywordChange = props["keywordChange"] || null;
    const filterComponent = props["filterComponent"] || null;
    const reloadTableRows = props["reloadTableRows"] || false;
    const updateTableRows = props["updateTableRows"] || null;
    const searchPlaceHolder = props["searchPlaceHolder"] || "";
    const saveSelectedRows = props["saveSelectedRows"] || null;
    const importBulkUpdate = props["importBulkUpdate"] || false;
    const [selectedRowsCount, setSelectedRowsCount] = useState(0);
    const handleSelectedRows = props["handleSelectedRows"] || null;
    const customSearchButton = props["customSearchButton"] || null;
    const selectedRowsUpdate = props["selectedRowsUpdate"] || null;
    const columnCheckSelectAll = props["columnCheckSelectAll"] || undefined;
    const deselectedRowsUpdate = props["deselectedRowsUpdate"] || undefined;
    const columnCheckDeselectAll = props["columnCheckDeselectAll"] || null;
    const sizePerPageList = [
        {
            text: "100",
            value: 100,
        },
        {
            text: "250",
            value: 250,
        },
        {
            text: "500",
            value: 500,
        },
    ];

    let otherProps: any = {};
    if (isSearchable) { otherProps["useGlobalFilter"] = useGlobalFilter; }
    if (isSortable) { otherProps["useSortBy"] = useSortBy; }
    if (isExpandable) { otherProps["useExpanded"] = useExpanded; }
    if (pagination) { otherProps["usePagination"] = usePagination; }
    if (isSelectable) { otherProps["useRowSelect"] = useRowSelect; }

    const getFormattedSelectedRows = (selectedRows: [], deselectedRows: []) => {
        let mappedRows: any = {};
        if (selectAllFilter) {
            let deselectedPositions: number[] = [];
            if (deselectedRows) deselectedPositions = deselectedRows.map((row: any) => row.positon);
            data.table.forEach((row: any, index: number) => {
                if (!deselectedPositions.includes(index)) mappedRows[index] = true;
            });
        } else {
            if (selectedRows) selectedRows.forEach((row: any) => { mappedRows[row.positon] = true; });
        }
        return mappedRows;
    }
    const currentSelectedRows = selectedRows ? selectedRows[data.pageIndex] : [];
    const currentDeselectedRows = deselectedRows ? deselectedRows[data.pageIndex] : [];

    const dataTable = useTable(
        {
            columns: props["columns"],
            initialState: {
                pageIndex: data.pageIndex,
                pageSize: props["pageSize"] || 10,
                selectedRowIds: {
                    ...getFormattedSelectedRows(currentSelectedRows, currentDeselectedRows),
                },
            },
            data: table || [],
            loading: loading,
            maxHeight: maxHeight,
            autoHeight: autoHeight,
            isSelectable: isSelectable,
            topbarChild: topbarChild,
            totalRows: data.totalRows,
            syncOptions: syncOptions,
            addNewRow: addNewRow,
            customAddButton: customAddButton,
            singleImport: singleImport,
            removeRows: removeRows,
            totalPages: data.totalPages,
            pageIndex: data.pageIndex,
            selectAllFilter: selectAllFilter,
            selectedRows: selectedRows,
            onSearchClick: onSearchClick,
            componentRef: componentRef,
            handleSingleEdit: handleSingleEdit,
            handleBulkEdit: handleBulkEdit,
            merge: merge,
            importProducts: importProducts,
            deselectedRows: deselectedRows,
            keywordChange: keywordChange,
            filterComponent: filterComponent,
            reloadTableRows: reloadTableRows,
            updateTableRows: updateTableRows,
            getExportFileBlob: getExportFileBlob,
            saveSelectedRows: saveSelectedRows,
            importBulkUpdate: importBulkUpdate,
            searchPlaceHolder: searchPlaceHolder,
            handleDelete: () => setShowPopup(true),
            selectedRowsUpdate: selectedRowsUpdate,
            customSearchButton: customSearchButton,
            columnCheckSelectAll: columnCheckSelectAll,
            deselectedRowsUpdate: deselectedRowsUpdate,
            columnCheckDeselectAll: columnCheckDeselectAll,
            // @ts-ignore           
            handleSelectedRows: () => handleSelectedRows([...handleSelectedRows.map((row: any) => row.original)]),
            pageCount: data.totalPages,
            manualPagination: true,
        },
        otherProps.hasOwnProperty("useGlobalFilter") && otherProps["useGlobalFilter"],
        otherProps.hasOwnProperty("useSortBy") && otherProps["useSortBy"],
        otherProps.hasOwnProperty("useExpanded") && otherProps["useExpanded"],
        otherProps.hasOwnProperty("usePagination") && otherProps["usePagination"],
        otherProps.hasOwnProperty("useRowSelect") && otherProps["useRowSelect"],
        useExportData,
        (hooks) => {
            isSelectable && hooks.visibleColumns.push((columns) => [
                {
                    id: "selection",
                    Header: ({ getToggleAllPageRowsSelectedProps }: any) => (
                        <div><IndeterminateCheckbox {...getToggleAllPageRowsSelectedProps()} onSelectAll={columnCheckSelectAll} onDeselectAll={columnCheckDeselectAll} /></div>
                    ),
                    maxWidth: 20,
                    minWidth: 20,
                    width: 20,
                    Cell: ({ row }: any) => (
                        <div><IndeterminateBodyCheckbox {...row.getToggleRowSelectedProps()} /></div>
                    ),
                },
                ...columns,
            ]);
        }
    );

    useEffect(() => {
        if (dataTable.state.pageIndex !== data.pageIndex || dataTable.state.pageSize !== data.pageSize) dataTable.updateTableRows(dataTable.state);
    }, [dataTable.state.pageIndex, dataTable.state.pageSize]);

    useEffect(() => {
        if (dataTable.data.length > 0 && dataTable.selectedFlatRows) {
            let deselectRows: any[] = [];
            let selectedIndexes: any[] = [];
            dataTable.selectedFlatRows.forEach((row: any) => selectedIndexes.push({ positon: row.index, id: row.original.id }));
            let selectedIndexesIds = selectedIndexes.map((row: any) => row.id);
            dataTable.data.map((row: any, index: number) => {
                if (!selectedIndexesIds.includes(row.id)) deselectRows.push({ positon: index, id: row.id });
            });
            if (dataTable.selectedRowsUpdate !== undefined) dataTable.selectedRowsUpdate(selectedIndexes, dataTable.pageIndex);
            if (dataTable.deselectedRowsUpdate !== undefined) dataTable.deselectedRowsUpdate(deselectRows, dataTable.pageIndex);
        }
    }, [dataTable.state.selectedRowIds, dataTable.state.pageIndex, dataTable.table]);

    // @ts-ignore
    function getExportFileBlob({ columns, data, fileType, fileName }) {
        if (fileType === "csv") {
            const headerNames = columns.map((col: any) => col.exportValue);
            const csvString = Papa.unparse({ fields: headerNames, data });
            return new Blob([csvString], { type: "text/csv" });
        } else if (fileType === "xlsx") {
            const header = columns.map((c: any) => c.exportValue);
            const compatibleData = data.map((row: any) => {
                const obj: any = {};
                header.forEach((col: any, index: any) => {
                    obj[col] = row[index];
                });
                return obj;
            });
            let wb = utils.book_new();
            let ws1 = utils.json_to_sheet(compatibleData, {
                header,
            });
            utils.book_append_sheet(wb, ws1, "React Table Data");
            writeFile(wb, `${fileName}.xlsx`);
            return false;
        }
        if (fileType === "pdf") {
            const headerNames = columns.map((column: any) => column.exportValue);
            const doc = new JsPDF();
            autoTable(doc, {
                head: [headerNames],
                body: data,
                margin: { top: 20 },
                styles: {
                    minCellHeight: 9,
                    halign: "left",
                    fontSize: 11,
                },
            });
            doc.save(`${fileName}.pdf`);
            return false;
        }
        return false;
    }

    useEffect(() => {
        let rows = 0;
        if (!dataTable.selectedRows) return setSelectedRowsCount(0);
        if (dataTable.selectAllFilter) {
            let deselectedRows = 0;
            dataTable.deselectedRows.forEach((row: any) => deselectedRows = deselectedRows + row.length);
            rows = dataTable.totalRows - deselectedRows;
        } else {
            dataTable.selectedRows.forEach((row: any) => rows = rows + row.length);
        }
        setSelectedRowsCount(rows);
    }, [dataTable.selectedRows]);

    function closeDeletePopup() {
        setIsDeleting(false);
        setShowPopup(false);
    }

    let rows = pagination ? dataTable.page : dataTable.rows;
    return (
        <>
            <Toolbar dataTable={dataTable} data={data} isSearchable={isSearchable} disableExport={disableExport} selectedRowsCount={selectedRowsCount} />
            <div
                className={classNames("table-responsive", "scrollable-container", className, autoHeight ? "ztable-auto" : "")}
                style={{ height: `${maxHeight}px` || "", overflow: "auto" }}
                ref={componentRef}
            >
                <Table
                    striped={true}
                    {...dataTable.getTableProps()}
                    className={classNames("table table-centered react-table", props["tableClass"])}
                >
                    <thead className={props["theadClass"]}>
                        {dataTable.headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column: any) => (
                                    <th
                                        {...column.getHeaderProps(
                                            column.defaultCanSort && column.getSortByToggleProps()
                                        )}

                                        {...column.getHeaderProps({
                                            style: { minWidth: column.minWidth, width: column.width },
                                        })}
                                        className={classNames({
                                            sorting_desc: column.isSortedDesc === true,
                                            sorting_asc: column.isSortedDesc === false,
                                            sortable: column.defaultCanSort === true,
                                        })}
                                    >
                                        {column.render("Header")}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...dataTable.getTableBodyProps()}>
                        {(rows || []).map((row: any, i: any) => {
                            dataTable.prepareRow(row);
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map((cell: any) => {
                                        return <td {...cell.getCellProps()}>{cell.render("Cell")}</td>;
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>

                {rows.length === 0 && (
                    <div className={classNames("text-center d-flex", autoHeight ? "" : "no-data")} style={{ flexDirection: "column" }}>
                        {data.error === "" && dataTable.loading === false && <p className="text-muted mb-0">No data to display</p>}

                        {data.error && dataTable.loading === false && <>
                            <i className="mdi mdi-alert mdi-18px" style={{ color: "red" }}></i>
                            <p className="text-muted mb-0 mt-2">{data.error}</p>
                        </>}

                        {dataTable.loading === true && <>
                            <Spinner className="m-2" color="primary" />
                            <p className="text-muted mb-0 mt-2">Loading</p>
                        </>}
                    </div>
                )}
            </div>

            {pagination && <Pagination tableProps={dataTable} sizePerPageList={sizePerPageList} />}

            {dataTable.handleDelete !== false && 
                // <Modal
                //     title="Confirm"
                //     show={showPopup}
                //     backdrop={isDeleting ? "static" : true}
                //     buttonText="Delete"
                //     buttonType="danger"
                //     buttonCloseText="Cancel"
                //     handleClose={() => setShowPopup(false)}
                //     handleSubmit={() => {
                //         setIsDeleting(true);
                //         // @ts-ignore
                //         removeRows(closeDeletePopup());
                //     }}
                //     disableButtons={isDeleting}
                //     description={
                //         <>
                //             <p>You are about to delete {selectedRowsCount} {selectedRowsCount > 1 ? "items": "item"} selected.</p>
                //             <p>Are you sure you want to delete?</p>
                //         </>
                //     }
                // />

                //
                //<p>You are about to delete {selectedRowsCount.toLocaleString("en-US")} selected {selectedRowsCount !== 1 ? "items": "item"}. This will unlink {selectedRowsCount > 1 ? "them": "it"} from Z Freedom, however will not delete any data at a store level.
                //Are you sure you want to delete?</p>
                <Modal backdrop={isDeleting ? "static" : true} size="sm" centered show={showPopup} onHide={closeDeletePopup} className="modal-mdlg">
                    <Modal.Header>
                        <Modal.Title>Confirm</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className={`${isDeleting && "disabled-div"}`}>
                        <BSRow>
                            <p>You are about to delete {selectedRowsCount.toLocaleString("en-US")} {selectedRowsCount > 1 ? "items": "item"} selected.
                             Are you sure you want to delete?</p>
                        </BSRow>
                        <BSRow className="pt-2 mt-1 border-top">
                            <Col sm={12} className="d-flex flex-row-reverse">
                                <Button style={{alignItems: "center"}}  variant="danger" className="btn ms-1 d-flex" disabled={isDeleting} onClick={() => { 
                                    if (removeRows) {
                                        setIsDeleting(true);
                                        removeRows(closeDeletePopup);
                                    }
                                }}>
                                    {isDeleting ? <><Spinner className="spinner-border-sm me-1" tag="span" color="white" /></> : <></>}
                                    {isDeleting ? <>Deleting...</> : <>Delete</>}
                                </Button>

                                <Button variant="secondary" className="btn ms-1" disabled={isDeleting} onClick={closeDeletePopup}>Cancel</Button>    
                                <TimedMessage message="Please wait while we delete your items..." delay={1} enabled={isDeleting} className="mb-0 py-1 px-2" />
                            </Col>
                        </BSRow>
                    </Modal.Body>
                </Modal>
            }
        </>
    );
};